import type {
  ChartType,
  MetricValues,
  SelectableChartType,
} from '../../lib/dashboard-capabilities';
import { SENTIMENT_ASPECT_METRICS } from '../sentiment-aspects';

/**
 * The AI <-> product chart-capability CONTRACT, shared by both sides of the
 * assistant (same single-source pattern as `sentiment-aspects`):
 *
 *  - apps/dash-meta derives its intent/schema chart-type enum and the
 *    compiler's requires-a-segment rule from here;
 *  - the dashboard app derives its LLM-dispatch requires-a-segment gate from
 *    here.
 *
 * WHY: these lists were hand-mirrored (two independent copies of the
 * requires-segment four — `intent-compiler.service.ts`'s
 * `chartRequiresDimension` and `llm-tab-content-dispatch.ts`'s
 * `chartTypesWithSecondaryDimension` — plus `INTENT_DATA_CHART_TYPES` and the
 * prose chart-type unions repeated across the dash-meta prompts). Nothing
 * checked them against the product's own capability registries, so a
 * capability the product shipped could stay silently unreachable by the AI —
 * the 2026-08-25 demo's "share" bug, where the chart and the data both
 * existed and only the AI's vocabulary was missing. The spec beside this file
 * iterates `ALL_CHART_TYPES` and fails on any unclassified member, converting
 * silent drift into a build failure.
 *
 * KNOWN, DELIBERATE non-unification: the FE chart-selector-config marks
 * `forceSegment: true` on {stacked-bar, grouped-bar, diverging-bar,
 * diverging-bar-combo} — a DIFFERENT four than CHART_TYPES_REQUIRING_SEGMENT.
 * They express different concepts (UI segment non-clearability vs the
 * data-shape requirement used for auto-fill). Reconciling them is a separate
 * follow-up; this module freezes today's behavior.
 */

/**
 * Chart types page-gen and the single-chart intent may ask the compiler for.
 * ORDER IS LOAD-BEARING: dash-meta builds a Zod enum from this array and the
 * digest/prompt surfaces must stay byte-stable — append, never reorder.
 */
export const AI_SELECTABLE_CHART_TYPES = [
  'bar',
  'line',
  'stacked-bar',
  'grouped-bar',
  'diverging-bar',
  'diverging-bar-combo',
  'map',
  'table',
  'treemap',
  'sankey',
  'chord',
  'box',
  'distribution',
  'volume',
] as const satisfies readonly [SelectableChartType, ...SelectableChartType[]];

export type AiSelectableChartType = (typeof AI_SELECTABLE_CHART_TYPES)[number];

/**
 * Every product chart type the AI may NOT select, each with the reason it is
 * withheld. The paired spec fails on any `ALL_CHART_TYPES` member that is in
 * neither list — adding a chart type to the product forces an entry here or
 * in the selectable list, in the same commit.
 *
 * Keyed by `ChartType` (not `string`) so a typo'd key is a compile error
 * wherever this module is typechecked. That does NOT replace the spec's
 * `phantomExclusions` check: libs/core's Jest runs through `@swc/jest`, which
 * strips types without checking them, so the runtime assertion stays the load-
 * bearing guard.
 */
export const AI_EXCLUDED_CHART_TYPES: Partial<Record<ChartType, string>> = {
  'share-of-segment':
    'Legacy composed type; the AI expresses shares as line+headcount+segment (share display). Stays parseable for existing configs via OUTPUT_ONLY_DATA_CHART_TYPES.',
  sentiment_review:
    'Review-text browser with a bespoke data pipeline (special route, no headless digest).',
  'gauge-v2':
    'Horizontal percentile gauge; needs gaugeV2 plot params PlotSchema does not carry (the intent compiler hard-maps it to table). Used by 9 templates.',
  'entity-summary':
    'Bespoke overview widget with its own data pipeline (special route); not a generatable analytical chart.',
  'mixed-stats':
    'Bespoke overview widget with its own data pipeline (special route); not a generatable analytical chart.',
  icicle:
    'Renders the nested composition breakdown (the selected entities, their first-level breakdown and the second-level breakdown beneath it) as a canvas icicle with click-to-drill navigation; special route with no headless digest, not a generatable analytical chart.',
  'subsidiary-snapshot':
    'Gauge, bar and diverging-bar cards beside the Subsidiaries page icicle (a Herfindahl–Hirschman concentration index of the direct subsidiaries\u2019 headcount per selected company, and subsidiaries acquired and divested over the whole history or up to the Date filter, from FactSet ownership history joined to company-mapper), fixed to the Subsidiaries page; special route with no headless digest, not a generatable analytical chart.',
  'subsidiary-trend':
    'Line trends of the selected companies\u2019 groups over time (subsidiary count, the Herfindahl–Hirschman concentration index of the direct subsidiaries\u2019 headcount, and subsidiaries acquired and divested per year, netted per company when comparing), derived from FactSet ownership history joined to company-mapper, fixed to the Subsidiaries page; special route with no headless digest, not a generatable analytical chart.',
  timeline:
    'Draws a corporate group over time in swimlanes: one coloured lane per direct subsidiary, its companies as lines that turn into their parent on the date they were acquired, from FactSet ownership history joined to company-mapper; special route with no headless digest, and it answers group history rather than any workforce metric.',
  'full-profile-info':
    'Talent-discovery profile widget; rendered as a table row popout, so it requires row-selection context the assistant does not have.',
  metric:
    "Render-time collapse of a single-value bar: ChartSelector's `metricShortcutPoints` rewrites chartType to 'metric' when an adaptive bar resolves to one SINGLE data point. Never authored directly.",
  text: 'Non-data content widget; report generation emits it through the report path (TextPlotSchema), not chart intent.',
  'ai-summary':
    'Report-only executive-summary widget (AiSummaryPlotSchema); generated by the report pipeline, not chart intent.',
  image:
    'Static content widget (report cover/branding assets); nothing analytical to generate.',
};

/**
 * Chart types whose DATA SHAPE requires a breakdown segment — used for
 * segment auto-fill by the dash-meta intent compiler and by the FE LLM
 * dispatch. A chart type NOT listed here may still carry an explicitly
 * requested segment (that is what makes breakdown/share lines expressible —
 * the 2026-08-25 bug was the FE treating this list as "may have a segment"
 * instead of "must have one").
 */
export const CHART_TYPES_REQUIRING_SEGMENT = [
  'grouped-bar',
  'stacked-bar',
  'box',
  'treemap',
] as const satisfies readonly [
  AiSelectableChartType,
  ...AiSelectableChartType[],
];

/**
 * Chartable-for-company metrics deliberately WITHHELD from the chart digest
 * (`entity-contexts.ts` BASE_METRICS), each with the reason. The parity spec
 * `entity-contexts.capability-parity.spec.ts` in dash-meta fails on any
 * company-chartable metric that is in neither list, so a metric added to the
 * capability matrix cannot stay silently unreachable by the AI.
 *
 * The sentiment-aspect block is the known gap: each aspect is answerable as a
 * single value by the sentiment read, but its trend/snapshot CHART — which
 * Company Sentiment ships — is not offered. That is a gap we are choosing to
 * record, not endorse.
 *
 * Two useful facts about the rest, both verifiable in libs/core:
 *  - 11 of the entries below are ALSO withheld from the product's own chart
 *    builder (`SUPPORTED_METRICS[metric] === false` in
 *    `dashboard-capabilities/availability.ts`); they reach the product only
 *    through a bespoke page/provider, never the generic chart config the AI
 *    drives. The matrix still reports them "chartable", which is why this
 *    list has to name them.
 *  - the rest are user-selectable but restate a digest metric in a
 *    table-only/peer-rank/text form (see per-entry reasons).
 *
 * Keyed by `MetricValues` (not `string`) so a typo'd key is a compile error
 * wherever this module is typechecked — same precedent as
 * `AI_EXCLUDED_CHART_TYPES`. That does NOT replace the spec's runtime
 * universe check: libs/core's Jest runs through `@swc/jest`, which strips
 * types without checking them.
 */
export const AI_EXCLUDED_CHART_METRICS: Partial<Record<MetricValues, string>> =
  {
    ...Object.fromEntries(
      SENTIMENT_ASPECT_METRICS.map((aspect) => [
        aspect,
        'Sentiment aspect: single-value answers only today; aspect CHARTS are a recorded gap (chartable in the matrix and shipped on the Company Sentiment page, not offered in the digest).',
      ])
    ),

    // --- Sentiment, but not a number ---------------------------------------
    positive_review:
      'Review-TEXT metric, not a score: it routes to SentimentReviewDataProvider, which returns a TextDataSet of review prose. Its chart is the `sentiment_review` browser already excluded in AI_EXCLUDED_CHART_TYPES, and its numeric shape probed empty (2026-07-07).',
    negative_review:
      'Review-TEXT metric, not a score: it routes to SentimentReviewDataProvider, which returns a TextDataSet of review prose. Its chart is the `sentiment_review` browser already excluded in AI_EXCLUDED_CHART_TYPES, and its numeric shape probed empty (2026-07-07).',

    // --- Transitions ------------------------------------------------------
    transitions_inflows_and_outflows:
      'Both-directions transitions variant behind the three-tier sankey/chord/diverging-bar-combo on the Transitions pages (its only chart types). The digest offers the single-direction pair transitions_inflow/transitions_outflow instead, and the intent compiler maps a `flow` analysis to a single-direction sankey. A RECORDED GAP: nothing on the AI side references this metric, so the both-directions view is unreachable.',

    // --- Pay percentile tables --------------------------------------------
    base_salary_percentiles:
      'Table-only percentile restatement of base_salary (SCREENER_TABLE_SHAPES / TABLE_ROWS; `table` is its only company chart type). base_salary is already in the digest with a distribution shape, and this variant exists to drive the compensation-benchmark REPORT template, which the report path builds directly.',
    total_compensation_percentiles:
      'Table-only percentile restatement of total_compensation (SCREENER_TABLE_SHAPES / TABLE_ROWS; `table` is its only company chart type). total_compensation is already in the digest with a distribution shape, and this variant exists to drive the compensation-benchmark REPORT template, which the report path builds directly.',
    contractor_pay_rate_percentiles:
      'Table-only percentile restatement of contractor_pay_rate (SCREENER_TABLE_SHAPES / TABLE_ROWS; `table` is its only company chart type). contractor_pay_rate is already in the digest with a distribution shape; the percentile table variant is a report/compensation-benchmark surface.',

    // --- Screener / Discovery table columns --------------------------------
    screener_headcount:
      'Screener table column, not a chart series: it is the peer-comparison headcount column of the Screener pages (TABLE_ROWS default). `headcount` covers the same figure in the digest, and Screener asks are served by navigating to the Screener page.',
    screener_growth:
      'Screener table column (TABLE_ROWS; `table` is its only company chart type). `growth_rate` covers the same figure in the digest; Screener asks are served by navigating to the Screener page.',
    screener_growth_yoy:
      'Screener table column (TABLE_ROWS; `table` is its only company chart type). It is the year-over-year variant of the same Screener growth column; `growth_rate` covers growth in the digest.',
    screener_hiring:
      'Screener table column (TABLE_ROWS; `table` is its only company chart type). `hiring_rate` covers the same figure in the digest; Screener asks are served by navigating to the Screener page.',
    screener_attrition:
      'Screener table column (TABLE_ROWS; `table` is its only company chart type). `attrition_rate` covers the same figure in the digest; Screener asks are served by navigating to the Screener page.',
    screener_salary:
      'Screener table column (TABLE_ROWS; `table` is its only company chart type). `salary` / `base_salary` cover pay in the digest; Screener asks are served by navigating to the Screener page.',
    screener_tenure:
      'Screener table column (TABLE_ROWS; `table` is its only company chart type). `tenure` covers the same figure in the digest; Screener asks are served by navigating to the Screener page.',
    screener_inflow:
      'Screener table column (TABLE_ROWS; `table` is its only company chart type). `transitions_inflow` covers joins in the digest; Screener asks are served by navigating to the Screener page.',
    screener_outflow:
      'Screener table column (TABLE_ROWS; `table` is its only company chart type). `transitions_outflow` covers departures in the digest; Screener asks are served by navigating to the Screener page.',

    // --- Not selectable in the product's own chart builder either -----------
    // (SUPPORTED_METRICS[metric] === false in dashboard-capabilities/availability.ts)
    td_users:
      'Talent Discovery profile-count metric; SUPPORTED_METRICS marks it false, so it is not selectable in the chart builder at all. It exists to drive the Talent Discovery / Posting Discovery table + map widgets, which have their own route.',
    entity_summary:
      'Bespoke Company/Role Summary widget payload (Tab.EntitySummary); SUPPORTED_METRICS marks it false. Its widget, `entity-summary`, is already excluded in AI_EXCLUDED_CHART_TYPES — there is no analytical series to generate.',
    overview_map:
      'Overview-tab composition map payload; SUPPORTED_METRICS marks it false and `map` is its only chart type. Geography breakdowns are expressed with `headcount` + a geography dimension instead.',
    market_share:
      'Competitive-landscape table column (SCREENER_TABLE_SHAPES / TABLE_ROWS — it has NO SINGLE shape at all, so a bar config for it would be substituted away); SUPPORTED_METRICS marks it false.',
    target_companies_rec:
      'A recommendation PAYLOAD from the competitive-landscape provider, not a figure; SUPPORTED_METRICS marks it false.',
    market_tightness:
      'Market Conditions metric; SUPPORTED_METRICS marks it false, so it is not chart-builder selectable. No read produces it either — the assistant offers the Market Conditions page instead of a figure. Market Conditions has no COMPANY page, and the value is a percentile against hard-coded labour-market reference distributions — charting it company-scoped would describe a company as if it were a market.',
    demand_supply:
      'Market Conditions metric; SUPPORTED_METRICS marks it false. It formats as FormatType.RATIO and the product never shows the raw ratio (the Demand/Supply gauge reads demand_supply_percentile and shows postings and profiles in its tooltip), so there is no honest number to plot or narrate.',
    demand_supply_percentile:
      'Market Conditions peer-rank restatement of demand_supply; SUPPORTED_METRICS marks it false. Percentile variants restate their base metric as a peer rank, and offering both invites the model to quote a rank as if it were the level.',
    wage_pressure:
      'Market Conditions metric; SUPPORTED_METRICS marks it false. Its value is NOT reliably the year-over-year figure its name promises: getWagePressureValue silently falls back to (last - first) / first over whatever window exists, and nothing downstream reports which branch produced the figure.',
    wage_pressure_percentile:
      'Market Conditions peer-rank restatement of wage_pressure; SUPPORTED_METRICS marks it false. It inherits the wage_pressure ambiguity AND the percentile hazard of quoting a rank as if it were the level.',
    subsidiary_count:
      'Company-mapper group-structure count, not a workforce series; SUPPORTED_METRICS marks it false. It is served by SubsidiaryMetricsDataProvider from the mapper rather than composition_v3, it ignores every page filter (date range included), and it exists only as a fixed tile on the Subsidiaries page.',
    time_to_fill_percentile:
      'Market Conditions peer-rank restatement of time_to_fill; SUPPORTED_METRICS marks it false. `time_to_fill` is already in the digest, and the percentile variant additionally has NO entry in COMBINATION_METRIC_REQUIREMENTS, so it would be served UNGATED.',
  };

/**
 * Every field of the product's manual chart config (`PlotOptions`, in
 * apps/reveliolabs-dashboard/.../PageConfigProvider/types/item.ts) classified
 * as either `'emittable'` — the AI may set it, and dash-meta's `PlotSchema`
 * mirrors it — or a written reason it is withheld.
 *
 * WHY THIS EXISTS: `PlotOptions` carries 54 fields; `PlotSchema` carries 8.
 * Nothing checked one against the other, so a capability the product ships
 * could stay silently unreachable by the AI forever — the 2026-08-25 demo's
 * "share of workforce" chart, which the product renders and the model had no
 * way to ask for. Classification converts that silence into a decision that
 * has to be written down.
 *
 * TWO-SIDED ENFORCEMENT, because neither side alone can see both types:
 *  - the FE (`AIAssistant/plot-field-contract-check.ts`) proves at COMPILE
 *    time that this object's keys are exactly `keyof PlotOptions` — apps
 *    cannot import apps, and only the dashboard app can see `PlotOptions`;
 *  - dash-meta (`tab-content.plot-contract.spec.ts`) proves at RUNTIME that
 *    `PlotSchema.shape` is exactly `AI_EMITTABLE_PLOT_FIELDS`.
 *
 * TYPING NOTE: unlike `AI_EXCLUDED_CHART_TYPES` this is NOT annotated
 * `Partial<Record<..., string>>`, for two independent reasons. (1) libs/core
 * cannot import `PlotOptions` to key by it — that type lives in an app. (2) An
 * annotation would widen the keys to `string` and erase the literal key types
 * the FE's phantom-field check (`Exclude<keyof typeof ..., keyof PlotOptions>`)
 * depends on. So it stays a bare `as const` literal; the FE check supplies the
 * key safety the annotation would have.
 *
 * "Emittable" records what IS, not what ought to be — `color`,
 * `showDataLabels` and `showZebraStripe` are cosmetic yet emittable because
 * `PlotSchema` already carries them. This module freezes today's behavior.
 */
export const AI_PLOT_FIELD_CLASSIFICATION = {
  // === EMITTABLE: mirrored by dash-meta's PlotSchema ======================
  chartType: 'emittable',
  defaultMetricOptions: 'emittable',
  // The compiler derives value/share from intent; downstream share checks and
  // the frontend honor the same explicit display mode.
  displayAs: 'emittable',
  secondaryDimension: 'emittable',
  plotFilters: 'emittable',
  dimensionSource: 'emittable',
  color: 'emittable',
  showDataLabels: 'emittable',
  showZebraStripe: 'emittable',
  breakdownLevels: 'emittable',

  // === ANALYTICAL LEVERS, WITHHELD =======================================
  // These change what a chart MEANS, not how it looks. Each is a candidate to
  // unlock; template counts are from CustomDash/templates/ (a lever many
  // hand-built templates reach for is a stronger candidate).
  shareOfSegment:
    'Analytical: with line + headcount + a segment this is what makes `isShareOfSegmentPlot` true — the share-of-workforce reading. Used by 5 templates. Withheld because assistant charts express value versus share through displayAs; a second independent switch could contradict that display mode.',
  groupBySegment:
    'Analytical: group-and-filter-data.ts aggregates rows into segment groups BEFORE TOP_N is applied, so it changes the numbers plotted, not their styling. Used by 5 templates. Unlock candidate, but it needs a paired rule about how it interacts with the emittable plotFilters.',
  hideOther:
    'Analytical: drops the "Other" bucket from the data (group-and-filter-data.ts, DynamicDivergingBarCombo), which changes totals and any share computed from them. Used by 11 templates — the most-used withheld lever, and a strong unlock candidate, but it silently changes a denominator, so it must not be offered without narration that says the remainder was removed.',
  comparisonTransformation:
    'Analytical: picks which entity is the baseline in a diverging comparison (transformComparisonData), which flips the direction/sign the chart reports. Used by 8 templates. Unlock candidate for two-entity comparisons, where the AI currently cannot say which side is the reference.',
  showQuartiles:
    'Analytical annotation on box/distribution charts: draws the quartile markers the reader would otherwise have to infer. Only 2 templates. Low-value unlock; withheld because the renderer default is deliberate and the AI has no basis to choose.',
  distributionCutoffLower:
    'Analytical-ish: trims the lower tail of the CDF (default 1st percentile) in Distribution.tsx, changing the visible range rather than the underlying figures. 5 templates. Withheld because the defaults are the honest view and a model-chosen cutoff could hide the tail that matters.',
  distributionCutoffUpper:
    'Analytical-ish: trims the upper tail of the CDF (default 99th percentile) in Distribution.tsx. 5 templates. Same reasoning as distributionCutoffLower — the defaults are the honest view.',

  // === TUNING BAGS FOR CHART TYPES THE AI *CAN* SELECT ====================
  // NB: treemap/sankey/chord/diverging-bar(-combo)/map are all in
  // AI_SELECTABLE_CHART_TYPES. The AI picks the chart type; it does not tune
  // the renderer. Compiler/renderer defaults apply to every field below.
  treemap:
    'Renderer tuning for a chart type the AI CAN select: `sizeTransform` (sqrt) rescales the tile areas. 5 templates. Withheld because a size transform makes areas no longer proportional to the value — a reader-misleading choice the model should not make silently.',
  sankey:
    'Renderer tuning for an AI-selectable chart type: ribbon width scale/exponents and the left/right flow captions. 4 templates. The width exponents distort apparent flow magnitude, and the captions are copy the compiler derives from the metric; renderer defaults apply.',
  chord:
    'Renderer tuning for an AI-selectable chart type: tick-label visibility and mode. 0 templates set it, and dash-meta does NOT persist it (no column on the plot-options entity), so an emitted value would be dropped on save.',
  divergingBar:
    'Renderer tuning for an AI-selectable chart type: arrow orientation and the positive/negative captions. 7 templates. The captions are copy the compiler derives from the metric direction; the AI chooses the chart, not its labels.',
  divergingBarCombo:
    'MIXED bag for an AI-selectable chart type, withheld as a unit: mostly captions and column sort, but `subfilterDimension` and `dynamicEntityComparison` are genuine analytical levers (they drive isDynamicDivergingBarComboPlot and change which comparison is computed). 5 templates. Unlocking the analytical two would mean splitting this bag rather than exposing it whole.',
  mapOptions:
    'MIXED bag for an AI-selectable chart type, withheld as a unit: `transformation` is cosmetic rescaling, but `normalization: area_density` is a genuine analytical lever — it switches a choropleth from raw counts to per-area density, which is a different claim about the map. 2 templates. Splitting the bag is the honest way to unlock the normalization half.',

  // === CONFIG BAGS FOR CHART TYPES THE AI CANNOT SELECT ==================
  // Each ties to an entry already recorded in AI_EXCLUDED_CHART_TYPES above.
  gaugeV2:
    'Gauge-v2 nested options; `gauge-v2` is excluded in AI_EXCLUDED_CHART_TYPES, so there is no AI-reachable chart to configure.',
  imageSrc:
    'Source for the `image` content widget, excluded in AI_EXCLUDED_CHART_TYPES (report cover/branding asset). Nothing analytical to configure.',
  textHtml:
    'Body of the `text` content widget, excluded in AI_EXCLUDED_CHART_TYPES. The report pipeline emits text through its own TextPlotSchema path, not chart intent.',
  textVAlign:
    'Vertical alignment of the `text` widget, excluded in AI_EXCLUDED_CHART_TYPES; cosmetic even within that widget.',
  icicle:
    'Size normalization for the `icicle` widget (`sizeTransform` square-roots cell sizes); the company subsidiary tree itself is chosen with the `subsidiaries` segment. `icicle` is excluded in AI_EXCLUDED_CHART_TYPES, so there is no AI-reachable chart to configure.',
  subsidiarySnapshot:
    'Chooses which card the `subsidiary-snapshot` widget draws (concentration gauge, or acquisitions and divestitures); that chart type is excluded in AI_EXCLUDED_CHART_TYPES and only ships on the Subsidiaries page, so there is no AI-reachable chart to configure.',
  subsidiaryTrend:
    'Chooses which trend the `subsidiary-trend` widget draws (subsidiary count, concentration, or acquisitions and divestitures per year); that chart type is excluded in AI_EXCLUDED_CHART_TYPES and only ships on the Subsidiaries page, so there is no AI-reachable chart to configure.',
  tableRowPopout:
    'Chart type shown when a table row is expanded; it defaults to `full-profile-info`, which is excluded in AI_EXCLUDED_CHART_TYPES because it needs row-selection context the assistant does not have. dash-meta does not persist this field either.',

  // === REPORT PIPELINE, NOT CHART INTENT =================================
  textPrompt:
    "Report-path field: the AI DOES set this, but through llm-tab-content-dispatch's text/report branch (TextPlotSchema), not the chart PlotSchema this contract governs. Adding it here would create a second, conflicting way to author text widgets.",
  contentSize:
    'Report-path field: like textPrompt, the AI sets it via the text/report branch of llm-tab-content-dispatch, not chart intent. dash-meta does not persist it on the plot-options entity.',
  excludeFromExecutiveSummary:
    'Report-authoring opt-out read by TextChartOrchestrator when assembling the executive summary. It concerns report composition, not what a chart shows, and belongs to the report path.',

  // === COSMETIC / STYLING ================================================
  // The model must not style charts: renderer and compiler defaults apply,
  // and every one of these is reachable by hand in the chart-config panel.
  colorPalette:
    'Cosmetic: multi-series colour ramp for grouped-bar/treemap. The model must not style charts; the renderer palette is theme-aware and the AI has no basis to override it.',
  sort: 'Cosmetic ordering (ASC/DESC) of an already-selected set — it reorders bars, it does not change which bars exist (that is plotFilters, which IS emittable). dash-meta does not persist it either.',
  maxValue:
    'Cosmetic axis bound on Bar; a model-chosen ceiling truncates bars and exaggerates differences, which is exactly the styling judgement the AI should not make.',
  minValue:
    'Cosmetic axis bound on Bar; a non-zero floor visually exaggerates differences. dash-meta does not persist it either.',
  showLegend: 'Cosmetic: legend visibility. Renderer default applies.',
  showDiffLabel:
    'Cosmetic line-chart annotation (diff between the first two series). Renderer default applies.',
  yAxisMin:
    'Cosmetic line-chart axis bound (handlebars-templated string). Same exaggeration hazard as minValue.',
  yAxisMax:
    'Cosmetic line-chart axis bound (handlebars-templated string). Same truncation hazard as maxValue.',
  curveSmoothness:
    'Cosmetic: line interpolation tension. Smoothing can imply data points that do not exist, so the renderer default stands.',
  fontSize: 'Cosmetic typography override. Renderer/theme default applies.',
  format:
    'Cosmetic value formatting override (FormatType). The format is DERIVED from the metric (and from displayAs on lines), so a model-set override could label a percentage as an integer; deriving it is the safer default.',
  orientation:
    'Cosmetic grouped-bar direction (horizontal/vertical). Renderer default applies. Only 1 template sets it.',

  // === DATA-SOURCE ROUTING ===============================================
  dataSource:
    "Assistant charts are mw-gql only. `snowflake` routes the widget to a customer's saved Snowflake query, which the assistant can neither discover nor validate, and whose entitlement model is separate.",
  snowflakeConfig:
    'Saved-query id plus per-field bindings for the snowflake dataSource above. Assistant charts are mw-gql only, so there is no query for the AI to bind.',

  // === OWNED BY THE FE DISPATCH LAYER, NOT THE MODEL =====================
  adaptive:
    'Set BY the FE, not the model: llm-tab-content-dispatch.ts marks a single-value AI bar (no dimensionSource, no secondaryDimension) `adaptive: true` so it collapses to a metric tile matching the chat preview. Letting the model set it would let it contradict the dispatch rule that keeps breakdown bars as bars.',

  // === TABLE / REPORT-PARAMETER CONFIGURATION ============================
  // Not a cosmetic family: tableColumns, isSingleEntity and
  // plotParameters each change the QUERY (columns are the table's query
  // spec; the switcher narrows the primary filter; grouped parameters
  // average the series). They are grouped here because they all belong to
  // the table/report authoring path rather than to chart intent.
  tableColumns:
    'Analytical, and the largest single withheld surface: for a custom table the column list IS the query spec — `deriveQueryFromColumns` reads the metrics, dimension source and secondary dimension straight off it, and each column carries its own aggregation (sum/avg/min/max/count). AI tables derive columns from the emittable metric + dimension instead. Unlocking it means exposing a second query language to the model, so it is withheld as a unit rather than as a formatting nicety.',
  isSingleEntity:
    'Analytical: `isSingleEntityChart` reads it to force the chart into SINGLE-ENTITY mode, narrowing the primary filter to one entity (DataProvider builds a single-entity override) as well as rendering the entity-flip control. 8 templates, and NO chart-config control sets it — it is template-authored only. Withheld because the assistant already scopes charts through filters; a second, implicit scoping mechanism could silently contradict them.',
  selectorOverrides:
    'Overrides which chart-selector controls are visible; it is populated from SAVED QUERY metadata (apply-saved-query-selection.ts), i.e. derived from the snowflake path the assistant does not use. dash-meta does not persist it either.',
  enableRowSelection:
    'Table interaction affordance: shows the row checkboxes and the selection action bar (TableChart defaults it ON — `!== false` — and the report variant forces it off regardless). It changes what a reader can do with a built table, not what the table reports.',
  plotParameters:
    "Analytical AND report-scoped: it selects which report parameters feed the widget's effective filters (changing the query), and a parameter marked `grouped: true` makes `aggregateParameterData` AVERAGE the series across that parameter's selections. It belongs to the report-template authoring path; assistant charts carry concrete filters instead of parameter bindings.",
} as const;

/** Field names dash-meta's PlotSchema must expose — derived, never listed twice. */
export const AI_EMITTABLE_PLOT_FIELDS = Object.entries(
  AI_PLOT_FIELD_CLASSIFICATION
)
  .filter(([, cls]) => cls === 'emittable')
  .map(([field]) => field)
  .sort();
